import Location from '../../hooks/Location'
import useSettings from '../../hooks/Settings.jsx'
import { useFirestore, useFirestoreGeneral } from '../../firebase/useFirestore'
import DeepAnalysisAnchorHeader from '../../components/deepAnalysis/DeepAnalysisAnchorHeader'
import AnalysisActivityCard from '../../components/analysis/AnalysisActivityCard.jsx'
import AnalysisResearchCard from '../../components/analysis/AnalysisResearchCard.jsx'
import DeepAnalysisLayout from '../../components/deepAnalysis/DeepAnalysisLayout'
import DeepAnalysisEffectSections from '../../components/deepAnalysis/DeepAnalysisEffectSections'
import DeepAnalysisResultWithContext from '../../components/deepAnalysis/DeepAnalysisResultWithContext'
import DeepAnalysisPassthroughFrame from '../../components/deepAnalysis/DeepAnalysisPassthroughFrame'
import OutputCompareSection from '../../components/deepAnalysis/OutputCompareSection'
import DeepAnalysisAISection from '../../components/deepAnalysis/DeepAnalysisAISection'
import CompareSection from '../../components/deepAnalysis/CompareSection'
import ResearchCombineSection from '../../components/deepAnalysis/ResearchCombineSection'
import ResultFilterSection from '../../components/deepAnalysis/ResultFilterSection'
import OpenQuestionAISection from '../../components/deepAnalysis/OpenQuestionAISection'
import MsiResearchDataSection from '../../components/deepAnalysis/MsiResearchDataSection'
import MsiResearchDataTable from '../../components/deepAnalysis/MsiResearchDataTable'
import PageHeader from '../../components/layout/PageHeader.jsx'
import { useLocation, useHistory } from 'react-router-dom'
import { client } from '../../hooks/Client'
import { isSuperAdminTenant } from '../../utils/superAdminTenant'
import { getSectorInfo } from '../../helpers/meetstandaard/sectorCatalog'
import DeepAnalysisPeriodContext from '../../StateManagment/DeepAnalysisPeriodContext'
import useDeepAnalysisPeriod from '../../hooks/useDeepAnalysisPeriod'

/**
 * @typedef {object} AnchorTypeConfig
 * @property {string} collection - Firestore collection holding the anchor doc
 * @property {string} typeLabel - Dutch label for the anchor type
 * @property {string} titleField - Doc field holding the display name
 */

/** @type {Object<string, AnchorTypeConfig>} */
const TYPE_CONFIG = {
    effect: { collection: 'OutputEffects', typeLabel: 'Effect', titleField: 'Effect' },
    research: { collection: 'Research', typeLabel: 'Onderzoek', titleField: 'Title' },
    activity: { collection: 'Activities', typeLabel: 'Activiteit', titleField: 'Activity' },
}

/**
 * Deep-analysis page — route `/:client/deepanalysis/:type/:id`.
 *
 * One page for all deep analyses, scoped to a single anchor entity
 * (effect or research). Entered via the "Deep analysis" action on dashboard
 * cards. Sections per type grow over the feature's tasks:
 * programmatic charts, scatterplot, compare selector, AI analysis.
 */
const DeepAnalysisPage = () => {
    const type = Location()[3]
    const id = Location()[4]
    const config = TYPE_CONFIG[type]
    const { search } = useLocation()
    const history = useHistory()
    const searchParams = new URLSearchParams(search)
    const sector = searchParams.get('sector') || ''
    // Datapunt-filters voor de onderliggende-data-weergave (klik in de analyse)
    const effectFilter = searchParams.get('effect') || ''
    const itemFilter = searchParams.get('item') || ''
    // Period + compare period from the url (?van=…&tot=…&vvan=…&vtot=…);
    // no query = all data. AI-CONTEXT: See docs/features/deep-analysis-period.md
    const period = useDeepAnalysisPeriod()

    const settings = useSettings()
    const personasEnabled = settings.personasOn === 'flex'
    const personas = useFirestore('Personas')

    // '' while the snapshot is loading, [] when the ID matches nothing.
    // When the type is unknown we pass an empty id, which skips the listener.
    const anchorDocs = useFirestoreGeneral(
        config ? config.collection : 'OutputEffects',
        'ID',
        config ? (id || '') : ''
    )
    // Effect links can also carry a meetstandaard ID (e.g. from the MSI
    // benchmarks card) — fall back to the tenant's effect with that MSIId.
    const msiFallbackDocs = useFirestoreGeneral(
        'OutputEffects',
        'MSIId',
        type === 'effect' ? (id || '') : ''
    )
    const stillLoading = (docs) => docs === ''
    const loading = Boolean(config && id) && (
        stillLoading(anchorDocs) || (type === 'effect' && stillLoading(msiFallbackDocs))
    )
    const anchor = (Array.isArray(anchorDocs) ? anchorDocs[0] : undefined)
        || (type === 'effect' && Array.isArray(msiFallbackDocs) ? msiFallbackDocs[0] : undefined)

    const renderSections = () => {
        switch (type) {
            case 'activity':
                return (
                    <DeepAnalysisLayout>
                        <DeepAnalysisResultWithContext
                            aboutTitle='Over deze activiteit'
                            description='De gemeten resultaten van deze activiteit, over alle deelnemers binnen de gekozen weergave.'
                        >
                            <AnalysisActivityCard
                                activity={anchor}
                                personas={personas}
                                personasEnabled={personasEnabled}
                                frameless
                                frameComponent={DeepAnalysisPassthroughFrame}
                            />
                        </DeepAnalysisResultWithContext>
                        <OutputCompareSection activity={anchor} />
                        <DeepAnalysisAISection type='activity' anchor={anchor} />
                    </DeepAnalysisLayout>
                )
            case 'effect':
                return (
                    <DeepAnalysisLayout>
                        <DeepAnalysisEffectSections
                            effect={anchor}
                            personas={personas}
                            personasEnabled={personasEnabled}
                        />
                    </DeepAnalysisLayout>
                )
            case 'research':
                return (
                    <DeepAnalysisLayout>
                        <DeepAnalysisResultWithContext
                            aboutTitle='Over dit onderzoek'
                            aboutText={anchor.Description}
                            description='De gemeten resultaten van dit onderzoek, over alle deelnemers binnen de gekozen weergave.'
                        >
                            <AnalysisResearchCard
                                research={anchor}
                                personas={personas}
                                personasEnabled={personasEnabled}
                                frameless
                                frameComponent={DeepAnalysisPassthroughFrame}
                            />
                        </DeepAnalysisResultWithContext>
                        <CompareSection anchor={anchor} />
                        <ResearchCombineSection anchor={anchor} />
                        <ResultFilterSection anchor={anchor} />
                        <OpenQuestionAISection anchor={anchor} />
                        <DeepAnalysisAISection type='research' anchor={anchor} />
                    </DeepAnalysisLayout>
                )
            default:
                return null
        }
    }

    // Onderzoeksdata meetstandaard (superTenant) — cross-tenant, geen anchor doc.
    // De Cloud Function gate server-side; deze check voorkomt alleen een lege
    // pagina met permission-errors voor niet-superTenant gebruikers.
    // 'meetstandaard' = resultaten (alpha + factoranalyse), 'meetstandaarddata'
    // = de onderliggende dataweergave achter de doorklik.
    if (type === 'meetstandaard' || type === 'meetstandaarddata') {
        if (!isSuperAdminTenant(client)) {
            return (
                <div className='main'>
                    <div className='main-container'>
                        <div className='deep-analysis-container'>
                            <p className='deep-analysis-not-found'>
                                Deze pagina is alleen beschikbaar voor het Deccos-beheerteam.
                            </p>
                        </div>
                    </div>
                </div>
            )
        }
        return (
            <div className='main'>
                <div className='main-container'>
                    <div className='deep-analysis-container'>
                        <PageHeader
                            heading={type === 'meetstandaarddata' ? 'Onderliggende data' : 'Onderzoeksdata meetstandaard'}
                            subHeading={sector ? getSectorInfo(sector).label : 'Hele meetstandaard'}
                            description="Cronbach's alpha en factoranalyse over de MSI-effecten van alle organisaties."
                            AI={false}
                        />
                        <div className='deep-analysis-sections'>
                            {type === 'meetstandaarddata'
                                ? <MsiResearchDataTable
                                    sector={sector}
                                    effectFilter={effectFilter}
                                    itemFilter={itemFilter}
                                    onClearFilter={() => history.push(
                                        `/${client}/deepanalysis/meetstandaarddata${sector ? `?sector=${sector}` : ''}`
                                    )}
                                />
                                : <MsiResearchDataSection sector={sector} />}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className='main'>
            <div className='main-container'>
                <div className='deep-analysis-container'>
                    {!config &&
                        <p className='deep-analysis-not-found'>
                            Deze analyse bestaat niet. Ga terug naar het dashboard en open de analyse opnieuw.
                        </p>
                    }
                    {config && loading && <p>Gegevens worden geladen…</p>}
                    {config && !loading && !anchor &&
                        <p className='deep-analysis-not-found'>
                            Dit item is niet gevonden. Het is mogelijk verwijderd.
                        </p>
                    }
                    {config && !loading && anchor &&
                        <>
                            <DeepAnalysisAnchorHeader
                                typeLabel={config.typeLabel}
                                title={anchor[config.titleField] || ''}
                                description='Diepgaande analyse van resultaten, spreiding en voortgang.'
                            />
                            <DeepAnalysisPeriodContext.Provider value={period}>
                                <div className='deep-analysis-sections'>
                                    {renderSections()}
                                </div>
                            </DeepAnalysisPeriodContext.Provider>
                        </>
                    }
                </div>
            </div>
        </div>
    )
}

export default DeepAnalysisPage
